/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The kind of MCP surface that serves the skill.
 */
export const SkillLinkTargetKind = {
  McpServer: "mcp_server",
  MetaMcpServer: "meta_mcp_server",
  OrganizationGateway: "organization_gateway",
} as const;
/**
 * The kind of MCP surface that serves the skill.
 */
export type SkillLinkTargetKind = OpenEnum<typeof SkillLinkTargetKind>;

/**
 * A live link from a project skill to the MCP surface that serves it over MCP.
 */
export type SkillLink = {
  /**
   * When the link was created.
   */
  createdAt: Date;
  /**
   * The link ID.
   */
  id: string;
  /**
   * The MCP server that serves the skill, for an mcp_server link.
   */
  mcpServerId?: string | undefined;
  /**
   * The gateway that serves the skill, for a meta_mcp_server link.
   */
  metaMcpServerId?: string | undefined;
  /**
   * The pinned version, absent when serving the latest valid version.
   */
  pinnedVersionId?: string | undefined;
  /**
   * The display name of the linked skill.
   */
  skillDisplayName: string;
  /**
   * The linked skill ID.
   */
  skillId: string;
  /**
   * The canonical name of the linked skill.
   */
  skillName: string;
  /**
   * The kind of MCP surface that serves the skill.
   */
  targetKind: SkillLinkTargetKind;
  /**
   * The name of the MCP server or gateway, absent for the organization gateway.
   */
  targetName?: string | undefined;
};

/** @internal */
export const SkillLinkTargetKind$inboundSchema: z.ZodMiniType<
  SkillLinkTargetKind,
  unknown
> = openEnums.inboundSchema(SkillLinkTargetKind);

/** @internal */
export const SkillLink$inboundSchema: z.ZodMiniType<SkillLink, unknown> = z
  .pipe(
    z.object({
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      id: z.string(),
      mcp_server_id: z.optional(z.string()),
      meta_mcp_server_id: z.optional(z.string()),
      pinned_version_id: z.optional(z.string()),
      skill_display_name: z.string(),
      skill_id: z.string(),
      skill_name: z.string(),
      target_kind: SkillLinkTargetKind$inboundSchema,
      target_name: z.optional(z.string()),
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "mcp_server_id": "mcpServerId",
        "meta_mcp_server_id": "metaMcpServerId",
        "pinned_version_id": "pinnedVersionId",
        "skill_display_name": "skillDisplayName",
        "skill_id": "skillId",
        "skill_name": "skillName",
        "target_kind": "targetKind",
        "target_name": "targetName",
      });
    }),
  );

export function skillLinkFromJSON(
  jsonString: string,
): SafeParseResult<SkillLink, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SkillLink$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SkillLink' from JSON`,
  );
}
